import { MapsAPILoader } from '@agm/core';
import { Component, HostListener, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { buildSummary, FilterSummaryItem } from 'app/_utils/filter-summary.utils';
import { LiveSessionService } from 'app/_services/live-session.service';
import { NotificationService } from 'app/_services/notification.service';
import { SessionsService } from 'app/_services/sessions.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { environment } from 'environments/environment';
import { SortEvent } from 'primeng/api';
import { Subject, Subscription, timer } from 'rxjs';
import Swal from 'sweetalert2';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-live-sessions',
  templateUrl: './live-sessions.component.html',
})
export class LiveSessionsComponent implements OnInit, OnDestroy {
  // General - Variables
  public isMapView = false;
  private liveSessionObservable: any;
  public sessionOwner: boolean;
  public sessionData: any;
  public newNote = '';
  private sendNewNoteSubject: Subject<[string, any]> = new Subject<[string, any]>();
  private sendNewNoteSubscription: Subscription;
  public selectedDateTime: Date | null = null;
  public isEditing: boolean = false;

  // Map View - Variables
  public isWeatherView = false;
  public defaultLat = 38.463186;
  public defaultLng = 51.848735;
  public defaultZoom = 3; // when no markers on map
  public defaultScreenLoadMaxZoom = 8;
  public maxZoom = this.defaultScreenLoadMaxZoom; // set to null after markers loaded
  public markers: Marker[] = [];
  public latLngBounds: any = false; // false or LatLngBounds object
  public meteoBlueUrl: string;
  private mapInstance: any;
  private siteOffSets: SiteOffset[] = [];

  // map/list select dialog box
  public selectedWorkerSessionModal = false;
  public selectedLiveSession: any;
  private routeRole: any;

  // List View - Variables
  public liveSessionsCompleteList: any[] = [];
  public liveSessionsMainWorkerListFiltered: any[] = [];
  public liveSessionsMainWorkerList: any[] = [];
  public isExpanded = false;
  public canExpand = false;
  public expandKeys = {};
  private allRowKeys = {};

  // List view - toggle columns
  public tableOptions = {
    visibleColumns: [],
  }
  public activeFiltersSummary: { label: string, value: string }[] = [];
  public columnOptionsSidebarVisible: boolean;
  public togglableColumns = [
    { displayName: 'Company', default: false },
    { displayName: 'Phone', default: false },
    { displayName: 'Guests', default: false },
    { displayName: 'Site', default: true },
    { displayName: 'Asset', default: true },
    { displayName: 'Work Order', default: true },
    { displayName: 'Reason', default: true },
    { displayName: 'Last Check', default: true },
    { displayName: 'Interval', default: false },
    { displayName: 'Expected Check-Off Time', default: true },
    { displayName: 'Comments', default: true },
  ];

 public sessionTypes: any[] = [
  { label: 'All', value: undefined },
  { label: 'Site Session Only', value: 'SITE_ONLY' },
  { label: 'Work Order Session Only', value: 'WORK_ORDER_ONLY' }
];
  // Sorting List
  public defaultSortField = 'startTime';
  public defaultSortOrder = -1;
  private sortOrderToText = (sortOrder: number) => { return sortOrder === 1 ? 'asc' : 'desc'; }
  public selectedSort = [this.defaultSortField + ',' + this.sortOrderToText(this.defaultSortOrder)];

  // Filters
  public isFilterLoading: boolean;
  public filters = {
    worker: '',
    company: '',
    site: '',
    sessionType: '',
    waypoint: '',
    checkOffTime: '',
  };

  // Filter Buttons
  public checkOffTimeButtons = [
    { key: '', label: 'All', class: 'primary' },
    { key: 'Ending Soon', label: 'Ending soon', class: 'primary' },
    { key: 'Overdue', label: 'Overdue', class: 'danger' }
  ];

  public waypointButtons = [
    { key: '', label: 'All', class: 'primary' },
    { key: 'Confirmed', label: 'Confirmed', class: 'success' },
    { key: 'Confirmed (outside boundary)', label: 'Confirmed (outside boundary)', class: 'warning' },
    { key: 'Overdue', label: 'Overdue', class: 'danger' }
  ];

  constructor(
    private notificationService: NotificationService,
    private liveSessionService: LiveSessionService,
    private sessionsService: SessionsService,
    private mapsAPILoader: MapsAPILoader,
    private sanitizer: DomSanitizer,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private workOrderService: SiteWorkOrdersService,
    globals: Globals,
  ) {
    this.routeRole = globals.user.routeRole;

    this.activatedRoute.queryParams.subscribe((params) => {
      if (params.tab && params.tab === 'list') {
        this.isMapView = false;
      }

      if (params.waypoint) {
        this.filters.waypoint = params.waypoint;
        this.filterListOfSessions();
      } else if (params.checkOffTime) {
        this.filters.checkOffTime = params.checkOffTime;
        this.filterListOfSessions();
      }
    });

    this.sendNewNoteSubscription = this.sendNewNoteSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe(([newText]) => {
      this.addComment(newText, this.sessionData);
    });
  }

  ngOnInit() {
    this.setupColumnOptions();

    // straight away, and every 60 seconds
    this.liveSessionObservable = timer(0, 1000 * 60).subscribe(() => {
      if (!document.hidden) {
        this.getAllLiveSessions();
      }
    });
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (!document.hidden) {
      this.getAllLiveSessions();
    }
  }

  ngOnDestroy() {
    if (this.liveSessionObservable) {
      this.liveSessionObservable.unsubscribe();
    }

    if (this.sendNewNoteSubscription) {
      this.sendNewNoteSubscription.unsubscribe();
    }
  }

  private getAllLiveSessions() {
    this.liveSessionService.getAllLiveSessionsNoPagination(this.selectedSort).subscribe((data: any) => {

      this.liveSessionsCompleteList = [];
      this.liveSessionsMainWorkerList = [];
      const sessionsWithGeolocation = [];
      this.canExpand = false;

      const addtionalWorkerSessions = data.filter(session => !session.owner);
      if (addtionalWorkerSessions.length) {
        this.canExpand = true;
      }

      this.siteOffSets = [];

      data.forEach((liveSession) => {
        const rawLastCheckTime = liveSession.lastUserSessionHealthCheckTime;
        liveSession.site = liveSession.siteName;
        liveSession.pillClass = this.getHCPillClass(liveSession);
        liveSession.pillText = this.getHCPillText(liveSession);
        liveSession.placeholderLocation = false;

        if (liveSession.outsideGeoFence) {
          this.setDummyLocationNearSite(liveSession);
        }

        let hasLocation = liveSession.lastCheckLatitude !== null;

        if (!hasLocation && liveSession.promptHealthCheck && liveSession.owner) {
          this.setDummyLocationNearSite(liveSession);
          liveSession.placeholderLocation = true;
          hasLocation = true;
        }

        if (!hasLocation && rawLastCheckTime && liveSession.owner) {
          this.setDummyLocationNearSite(liveSession);
          liveSession.placeholderLocation = true;
          hasLocation = true;
        }

        liveSession.ecotOverdue = liveSession.expectedCheckOffTime
          ? new Date(liveSession.expectedCheckOffTime) < new Date()
          : false;

        liveSession.locationUnknown = !hasLocation && !liveSession.outsideGeoFence;
        liveSession.locationConfirmed = hasLocation && !liveSession.outsideGeoFence && !liveSession.placeholderLocation;
        liveSession.lastUserSessionHealthCheckTime = rawLastCheckTime ? new Date(rawLastCheckTime) : null;
        if (liveSession.owner) {
          liveSession.addWorkers = addtionalWorkerSessions.filter(a => (a.siteSessionId === liveSession.siteSessionId));
          this.liveSessionsMainWorkerList.push(liveSession);
          this.allRowKeys[liveSession['id']] = 1; // to enable expand/collapse all button
        }

        this.liveSessionsCompleteList.push(liveSession);

        // if there is a geo location, pass it to the markers method
        if (liveSession.lastCheckLatitude !== null || liveSession.outsideGeoFence) {
          sessionsWithGeolocation.push(liveSession);
        }
      });

      if (this.liveSessionsMainWorkerList.length + addtionalWorkerSessions.length !== this.liveSessionsCompleteList.length) {
        console.error('Mismatch in the additional worker filtering');
      }
      this.populateMarkers(sessionsWithGeolocation);
      this.filterListOfSessions();
    });
  }

  /**
   * If there are multiple workers mapped to the same site without a usable location
   * Then their locations will show on the site location and hide each other
   * Therefore offset them by a few meters
   */
  private setDummyLocationNearSite(liveSession: any) {
    const coordinateOffset = 0.0003;
    let offsetFactor = 1;
    // tslint:disable-next-line:prefer-const the variable is not reassigned, but it is changed: 'let' seems more appropriate
    let existingSiteOffSet = this.siteOffSets.find((siteOffset) => siteOffset.siteId === liveSession.siteId);
    if (existingSiteOffSet) {
      offsetFactor = ++existingSiteOffSet.offsetFactor;
    } else {
      this.siteOffSets.push({
        siteId: liveSession.siteId,
        offsetFactor: 1
      });
    }
    liveSession.lastCheckLatitude = liveSession.siteLatitude + (coordinateOffset * offsetFactor);
    liveSession.lastCheckLongitude = liveSession.siteLongitude + (coordinateOffset * offsetFactor);
  }

  private populateMarkers(sessions) {
    this.markers = [];

    sessions.forEach((session) => {
      this.markers.push({
        lat: session.lastCheckLatitude,
        lng: session.lastCheckLongitude,
        session: session,
        draggable: false,
        iconUrl: this.getIcon(session),
      });
    });

    this.mapsAPILoader.load().then(() => {
      this.toggleMaxZoom(true);

      if (this.markers.length) {
        this.latLngBounds = new window['google'].maps.LatLngBounds();
        this.markers.forEach((location) => {
          this.latLngBounds.extend(new window['google'].maps.LatLng(location.lat, location.lng));
        });
      } else {
        this.latLngBounds = false;
      }

      // after initial view frames the markers, remove the max zoom so user can zoom right in
      this.toggleMaxZoom(false);
    });
  }

  public getIcon(session) {
    const greenIcon = 'http://maps.google.com/mapfiles/ms/icons/green.png';
    const redIcon = 'http://maps.google.com/mapfiles/ms/icons/red.png';
    const orangeIcon = 'http://maps.google.com/mapfiles/ms/icons/orange.png';
    const blueIcon = 'http://maps.google.com/mapfiles/ms/icons/blue.png';

    if (session.promptHealthCheck || new Date(session.expectedCheckOffTime) < new Date()) {
      return redIcon;
    } else if (session.outsideGeoFence) {
      return orangeIcon;
    } else if (session.placeholderLocation) {
      return blueIcon;
    } else {
      return greenIcon;
    }

  }

  public mapViewChange(mapView) {
    if (mapView === 'Weather View') {
      this.isWeatherView = true;
    } else {
      this.isWeatherView = false;
    }
  }

  public mapReady(mapInstance) {
    this.mapInstance = mapInstance;
  }

  public boundsChange() {
    const loc = this.mapInstance.getCenter().lat() + 'N' + this.mapInstance.getCenter().lng() + 'E';
    let zoom = 10;
    // convert google zoom into meteo blue zoom
    if (this.mapInstance.getZoom() && this.mapInstance.getZoom() < 12) {
      zoom = this.mapInstance.getZoom() - 1;
    }
    this.meteoBlueUrl = environment.meteoblueWindUrl + loc
      + '?windAnimation=0&windAnimation=1&gust=0&gust=1&satellite=0&satellite=1&geoloc=fixed&tempunit=C'
      + '&windunit=m%252Fs&lengthunit=metric&autowidth=auto&zoom=' + zoom;
  }

  public clickedMarker(session, index) {
    this.selectedLiveSession = session;
    this.toggleSelectedWorkerSessionModal();

    this.sessionOwner = session.owner;
  }

  private toggleSelectedWorkerSessionModal() {
    this.selectedWorkerSessionModal = !this.selectedWorkerSessionModal;
  }

  public toggleSessionsView() {
    this.isMapView = !this.isMapView;
    this.isWeatherView = false;

    // re-enter map screen, refresh zoom
    if (this.isMapView) {
      this.toggleMaxZoom(true);
      this.toggleMaxZoom(false);
    }
  }

  // set a maxZoom so when map is refreshed it doesnt zoom too far in.
  // but disable the maxZoom after that, to enable user to zoom as much as they like.
  private toggleMaxZoom(on: boolean) {
    if (on) {
      this.maxZoom = this.defaultScreenLoadMaxZoom;
    } else {
      // delay necessary so it occurs after map is loaded
      setTimeout(() => { this.maxZoom = null; }, 100);
    }
  }

  public toggleExpandAllRows(toExpand: boolean) {
    if (toExpand) {
      Object.assign(this.expandKeys, this.allRowKeys);
      this.isExpanded = true;
    } else {
      this.expandKeys = {};
      this.isExpanded = false;
    }
  }

  // Highlight row if overdue a health check
  // Highlight red for expected check off times
  // add border to bottom if it is the last child of the parent
  public getAdditionalWorkerRowClass(index: number, liveSessions: any[]) {
    let cls = '';
    const liveSession = liveSessions[index];
    const expiryDate = new Date(liveSession.expectedCheckOffTime);
    const now = new Date();

    if (expiryDate < now) {
      cls += 'danger-row';
    } else if (liveSession.promptHealthCheck) {
      cls += 'warning-row';
    }

    if (index === liveSessions.length - 1) {
      cls += ' border-underneath';
    }
    return cls;
  }

  // Highlight the row if:
  // - the row is overdue a health check
  // - the expected check off time is overdue
  // - the row is a parent of a row that is overdue a health check (or expected check off time), but only when the child row is hidden
  public getMainRowClass(liveSession, isExpanded: boolean) {
    const parentSession = liveSession;
    const childrenSessions = liveSession.addWorkers;

    const expiryDate = new Date(parentSession.expectedCheckOffTime);
    const now = new Date();

    if (expiryDate < now) {
      return 'danger-row';
    } else if (parentSession.promptHealthCheck) {
      return 'warning-row';
    }

    // children
    for (let i = 0; i < childrenSessions.length; i++) {
      const session = childrenSessions[i];

      const expiryDate = new Date(session.expectedCheckOffTime);

      if (expiryDate < now && !isExpanded) {
        return 'danger-row';
      } else if (session.promptHealthCheck && !isExpanded) {
        return 'warning-row';
      }
    }
  }

  public customSort(sort: SortEvent) {
    const sortDirection = this.sortOrderToText(sort.order);

    this.selectedSort = [sort.field + ',' + sortDirection];

    this.getAllLiveSessions();
  }

  public resetColumnOptions() {
    this.tableOptions = {
      visibleColumns: this.togglableColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.liveSessions', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.liveSessions');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }

  public goToWorkOrder(liveSession) {
    const siteId = liveSession.siteId;
    const workOrderId = liveSession.workOrderId;

    this.router.navigate(['/' + this.routeRole + '/sites/' + siteId + '/work-orders/' + workOrderId]);
  }

  public getWhat3Words(session){
   this.selectedWorkerSessionModal = false;
   const footer = this.sanitizer.sanitize(SecurityContext.HTML, `This is the last known location for ${session.fullName}`);
    this.sessionsService.getWhat3WordsLocation(session.lastCheckLatitude, session.lastCheckLongitude).subscribe((data: any) => {
      Swal.fire({
        title: `What-3-Words Location`,
        text:  `///${data.words}`,
        type: `info`,
        footer: footer,
        confirmButtonText: `OK`,
      });
    });
  }

  public getHCPillClass(liveSession: any) {
    if (liveSession.promptHealthCheck) {
      return 'badge badge-pill badge-danger badge-border';
    } else if (!liveSession.promptHealthCheck && liveSession.outsideGeoFence) {
      return 'badge badge-pill badge-warning badge-border';
    } else if (!liveSession.promptHealthCheck && liveSession.lastCheckLatitude !== null) {
      return 'badge badge-pill badge-success';
    } else if (liveSession.lastCheckLatitude === null) {
      return 'badge badge-pill badge-info';
    }
  }

  public getHCPillText(liveSession: any) {
    if (liveSession.promptHealthCheck) {
      if (liveSession.lastCheckLatitude === null && !liveSession.outsideGeoFence) {
        return 'Overdue (unavailable)';
      }
      return 'Overdue';
    } else if (!liveSession.promptHealthCheck  && liveSession.outsideGeoFence) {
      return 'Confirmed (outside boundary)';
    } else if (!liveSession.promptHealthCheck  && liveSession.lastCheckLatitude !== null) {
      return 'Confirmed';
    } else if (liveSession.lastCheckLatitude === null) {
      return 'Unavailable';
    }
  }

  public onFilterTextChanged(filterText: string, filterField: string) {
    this.isFilterLoading = true;

    this.filters[filterField] = filterText;
    this.filterListOfSessions();
  }

  public onSessionTypeChanged(event) {
    this.isFilterLoading = true;
    this.filters.sessionType = event.value.value;
    this.filterListOfSessions();
  }



  public filterListOfSessions() {
    // Reset by cloning non-filtered list
    this.liveSessionsMainWorkerListFiltered = [...this.liveSessionsMainWorkerList];

    this.updateActiveFiltersSummary();

    if (this.filters.worker) {
      this.filterByWorker();
    }

    if (this.filters.company) {
      this.filterByCompany();
    }

    if (this.filters.site) {
      this.filterBySite();
    }

    if (this.filters.sessionType) {
      this.filterBySessionType();
    }

    if (this.filters.waypoint) {
      this.filterByWaypoint();
    }

    if (this.filters.checkOffTime) {
      this.filterByCheckOffTime();
    }

    this.isFilterLoading = false;
  }

  private updateActiveFiltersSummary() {
    const parts: FilterSummaryItem[] = [];

    if (this.filters.worker) {
      parts.push({ label: 'Worker contains', value: this.filters.worker });
    }

    if (this.filters.company) {
      parts.push({ label: 'Company contains', value: this.filters.company });
    }

    if (this.filters.site) {
      parts.push({ label: 'Site contains', value: this.filters.site });
    }

    if (this.filters.sessionType === 'WORK_ORDER_ONLY') {
      parts.push({ label: 'Session type', value: 'Work order sessions only' });
    } else if (this.filters.sessionType === 'SITE_ONLY') {
      parts.push({ label: 'Session type', value: 'Site sessions only' });
    }

    if (this.filters.waypoint) {
      const waypointSummary = buildSummary('Waypoint status', this.filters.waypoint, this.waypointButtons);
      if (waypointSummary) {
        parts.push(waypointSummary);
      }
    }

    if (this.filters.checkOffTime) {
      const checkOffSummary = buildSummary('Check-off time', this.filters.checkOffTime, this.checkOffTimeButtons);
      if (checkOffSummary) {
        parts.push(checkOffSummary);
      }
    }

    this.activeFiltersSummary = parts;
  }

  private filterByWorker() {
    const workerFilter = this.filters.worker.toLowerCase();

    this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
      const worker = `${session.fullName} ${session.login}`.toLowerCase();
      return worker.includes(workerFilter) || session.addWorkers.some(addWorkerSession =>
        `${addWorkerSession.fullName} ${addWorkerSession.login}`.toLowerCase().includes(workerFilter)
      );
    });
  }

  private filterBySessionType() {
    if(this.filters.sessionType === "WORK_ORDER_ONLY"){
      this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
        return session.workOrderId;
      });
    } else {
      if(this.filters.sessionType === "SITE_ONLY"){
      this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
        return !session.workOrderId;
      });
    }}
  }

  private filterByCompany() {
    const companyFilter = this.filters.company.toLowerCase();

    this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
      const company = session.maintenanceName.toLowerCase();
      return company.includes(companyFilter) || session.addWorkers.some(addWorkerSession =>
        addWorkerSession.maintenanceName.toLowerCase().includes(companyFilter)
      );
    });
  }

  public showCheckOffButtons(): boolean {
    return this.sessionOwner
      && this.selectedLiveSession.workOrderId
      && !this.selectedLiveSession.requiresTransferOfControl;
  }

  private filterBySite() {
    const siteFilter = this.filters.site.toLowerCase();

    this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
      const site = session.site.toLowerCase();
      return site.includes(siteFilter);
    });
  }

  private sessionMatchesLeadOrAdditionalWorkers(session: any, predicate: (target: any) => boolean) {
    if (predicate(session)) {
      return true;
    }

    if (session.addWorkers && session.addWorkers.length) {
      return session.addWorkers.some(worker => predicate(worker));
    }

    return false;
  }

  private filterByWaypoint() {
    switch (this.filters.waypoint) {
       case 'Confirmed':
        this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
          const isConfirmed = (target) => (!target.promptHealthCheck && target.lastCheckLatitude !== null && !target.outsideGeoFence);
          return this.sessionMatchesLeadOrAdditionalWorkers(session, isConfirmed);
        });
        break;
        case 'Confirmed (outside boundary)':
        this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
          const isConfirmedOutside = (target) => (!target.promptHealthCheck && target.outsideGeoFence);
          return this.sessionMatchesLeadOrAdditionalWorkers(session, isConfirmedOutside);
        });
        break;
        case 'Overdue':
        this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
          const isOverdue = (target) => target.promptHealthCheck;
          return this.sessionMatchesLeadOrAdditionalWorkers(session, isOverdue);
        });
        break;
    }
  }

  private filterByCheckOffTime() {
    const now = new Date().getTime();

    if (this.filters.checkOffTime === 'Ending Soon') {

      this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
        const isEndingSoon = (target) => {
          if (!target.expectedCheckOffTime) {
            return false;
          }
          const checkOffTime = new Date(target.expectedCheckOffTime).getTime();
          if (isNaN(checkOffTime)) {
            return false;
          }
          const diffMinutes = (checkOffTime - now) / (1000 * 60);
          // Return sessions that will be expire in 30 minutes
          return diffMinutes > 0 && diffMinutes <= 30;
        };

        return this.sessionMatchesLeadOrAdditionalWorkers(session, isEndingSoon);
      });
    }
    if (this.filters.checkOffTime === 'Overdue') {
        this.liveSessionsMainWorkerListFiltered = this.liveSessionsMainWorkerListFiltered.filter(session => {
          const isOverdue = (target) => {
            if (!target.expectedCheckOffTime) {
              return false;
            }
            const checkOffTime = new Date(target.expectedCheckOffTime).getTime();
            if (isNaN(checkOffTime)) {
              return false;
            }
            return checkOffTime < now;
          };

          return this.sessionMatchesLeadOrAdditionalWorkers(session, isOverdue);
        });
    }
  }

  public setCheckOffTimeFilter(key: string) {
    this.filters.checkOffTime = key;
    this.filterListOfSessions();
  }

  public setWaypointFilter(key: string) {
    this.filters.waypoint = key;
    this.filterListOfSessions();
  }

  public checkout(session, checkOutType) {
    const body = {
      siteSessionId: session.siteSessionId,
      workerUserId: session.skylarkUserId,
      workOrderStatus: checkOutType
    };

    let title = '';
    let text = '';
    let toastSuccess = '';

    const mainWorkerName = this.sanitizer.sanitize(SecurityContext.HTML, `${session.fullName} (${session.login})`);

    this.selectedWorkerSessionModal = false;

    if (session.workOrderId === null) {
      // Management Duties
      Swal.fire({
        title: `Checkout ${mainWorkerName}?`,
        text: 'This worker is carrying out a site session, are you sure you want to check them out?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Checkout!',
        customClass: {
          confirmButton: 'confirm-danger',
        }
      }).then((result) => {
        if (result.value) {
          this.sessionsService.checkoutManagement(body).subscribe(() => {
            this.getAllLiveSessions();
            this.notificationService.notify('success', 'Success!', 'Worker has been successfully checked out!');
          });
        }
      });
    } else {

      switch (checkOutType) {
        case 'CLOSED':
          title = `Close work order?`
          text = 'This will check out all the active workers'
          toastSuccess = 'Workers checked out - work order CLOSED';
          break;
        case 'SURRENDERED':
          title = `Check Out and Surrender?`
          text = 'This will check out all the active workers <br> <b>The asset will remain locked</b> until the current lead worker resumes the work order.'
          toastSuccess = 'Workers checked out - work order set to SURRENDERED';
          break;
        case 'SUSPENDED':
          title = `Check Out and Suspend?`
          text = 'This will check out all the active workers'
          toastSuccess = 'Workers checked out - work order set to SUSPENDED';
          break;
        default:
          break;
      }

      if (session.owner) {
        // Session owner, check out ALL workers on session
        let workersString = '';
        if (session.addWorkers.length) {
          session.addWorkers.forEach(worker => {
            const workerName = `${worker.fullName} (${worker.login})`;
            workersString += '<br>- ' + this.sanitizer.sanitize(SecurityContext.HTML, workerName);
          });
        } else {
          workersString += 'N/A';
        }
        Swal.fire({
          title: `${title}`,
          html: `${text}.<br><br>Lead Worker: ${mainWorkerName}<br><br>Additional Workers:<br>${workersString}`,
          type: 'warning',
          showCancelButton: true,
          customClass: {
            confirmButton: 'confirm-danger',
          },
          confirmButtonText: 'Yes',
        }).then((result) => {
          if (result.value) {
            this.addNote(checkOutType, toastSuccess, body)
          }
        });
      } else {
        // Session member, only check out this worker
        Swal.fire({
          title: `Checkout ${mainWorkerName}?`,
          text: 'This will checkout the selected worker from work order session',
          type: 'warning',
          showCancelButton: true,
          customClass: {
            confirmButton: 'confirm-danger',
          },
          confirmButtonText: 'Yes',
        }).then((result) => {
          if (result.value) {
            toastSuccess = 'Worker has been successfully checked out!'
            this.addNote(checkOutType, toastSuccess, body)
          }
        });
      }
    }
  }

  public async addNote(checkOutType, toastSuccess, body) {
    const workOrderId = this.selectedLiveSession.workOrderId

    let { value: notes } = await
      Swal.fire({
        titleText: 'Add note?',
        input: 'textarea',
        inputPlaceholder: 'Optional: Type your notes here...',
        showCancelButton: false,
      });
    if (notes) {
      body.notes = notes
    }
    if (checkOutType === 'CLOSED') {
      this.workOrderService.closeWorkOrder(workOrderId).subscribe((data: any) => {
        this.getAllLiveSessions();
        this.notificationService.notify('success', 'Closed!', `${toastSuccess}`);
      });
    }
    if (checkOutType === 'SUSPENDED' || checkOutType === 'SURRENDERED') {
      this.sessionsService.checkoutWorker(body).subscribe((data: any) => {
        this.getAllLiveSessions();
        this.notificationService.notify('success', 'Success!', `${toastSuccess}`);
      });
    }
  }

  addComment(newText: string, sessionData) {
    const notes = newText.trim();
    const session = sessionData

    if (!notes) {
      this.notificationService.notify('danger', 'Comments field cannot be updated empty');
    } else {
      const body = {
        userSessionId: session.id,
        notes: notes,
      };

      this.sessionsService.sendNewNotes(body).subscribe(
        (data: any) => {
          this.notificationService.notify('success', 'Success', 'Comments updated');
        },
        (error) => {
          console.error('Error', error);
          this.notificationService.notify('danger', 'Comments cannot be updated');
        }
      );
    }
  }

  onTextChange(newText, sessionData) {
    this.sessionData = sessionData;
    this.newNote = newText.target.value
    this.sendNewNoteSubject.next([this.newNote, this.sessionData]);
  }

  public enableEditing() {
    this.isEditing = true;

    if (this.selectedLiveSession.expectedCheckOffTime) {
      const existingTime = new Date(this.selectedLiveSession.expectedCheckOffTime);
      this.selectedDateTime = new Date();
      this.selectedDateTime.setHours(existingTime.getHours(), existingTime.getMinutes(), 0, 0);
    }
  }

  public disableEditing() {
    this.isEditing = false;
    this.selectedDateTime = null;
  }

  public updateCheckOffTime() {
    const userSessionId = this.selectedLiveSession.id;

    if (!this.selectedDateTime || isNaN(this.selectedDateTime.getTime())) {
      this.notificationService.notify('warning', 'Time cannot be empty or incorrectly formatted!');
      return;
    }

    const updatedTime = new Date(this.selectedLiveSession.expectedCheckOffTime);
    updatedTime.setHours(this.selectedDateTime.getHours(), this.selectedDateTime.getMinutes(), 0, 0);

    if (userSessionId) {
      this.sessionsService.updateExpectedCheckOffTime(userSessionId, updatedTime).subscribe(
        (data) => {
          this.selectedLiveSession.expectedCheckOffTime = updatedTime;
          this.notificationService.notify('success', 'Time updated!');
          this.getAllLiveSessions();
          this.disableEditing();
        },
        (error) => {
          console.error('Error', error);
          this.notificationService.notify('danger', 'Date cannot be updated', error.error.title);
          this.getAllLiveSessions();
        }
      );

    } else {
      console.error('User session ID is missing!');
      this.notificationService.notify('danger', 'User session ID is missing!');
    }
  }

  // Toggle dropdown using enter/space. Older version of primeNG doesn't support this, so we need to do it manually.
  public toggleDropdown(event: KeyboardEvent) {
    const dropdown = event.currentTarget as HTMLElement;
    const trigger = dropdown.querySelector('.p-dropdown-trigger') as HTMLElement;
    const panel = document.querySelector('.p-dropdown-panel') as HTMLElement;
    if (panel && panel.offsetParent !== null) {
      document.body.click(); // Close the dropdown
    }
    else if (trigger) {
      trigger.click(); // Open the dropdown
    }
    event.preventDefault();
  }

}

interface Marker {
  lat: number;
  lng: number;
  session: any;
  draggable?: boolean;
  iconUrl: string;
}

interface SiteOffset {
  siteId: number;
  offsetFactor: number;
}
